/* SynonymsPanel — admin UI for the search_synonyms expansion dictionary.
 *
 * - Lists every cluster (searchable by any term in the cluster).
 * - Preview-expand any term to see exactly what search uses it for
 *   (same `/api/synonyms/expand` endpoint the search path hits).
 * - Create / edit / delete clusters.
 * - "AI top-up" button asks Claude Sonnet for N more clusters and
 *   merges them in (idempotent — safe to re-run).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MagnifyingGlass, Plus, Trash, Sparkle, PencilSimple, Check, X } from "@phosphor-icons/react";

function EmptyRow({ onAdd }) {
  return (
    <div className="rounded-2xl bg-rf-cream-alt p-10 text-center" data-testid="synonyms-empty">
      <div className="text-5xl mb-3" aria-hidden>🔤</div>
      <h2 className="font-display font-black text-xl text-rf-ink mb-2">No clusters yet</h2>
      <p className="text-rf-ink-soft text-sm mb-5">
        Clusters teach search that &ldquo;drink&rdquo;, &ldquo;cocktail&rdquo; and &ldquo;beverage&rdquo; mean the same thing. Seed a starter set with AI or add your first cluster below.
      </p>
      <button type="button" onClick={onAdd} className="btn-amber">
        <Plus size={16} /> Add cluster
      </button>
    </div>
  );
}

export default function SynonymsPanel() {
  const [clusters, setClusters] = useState(null);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null); // {cluster_id?, canonical, terms:string}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (q = "") => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/synonyms${params.toString() ? `?${params}` : ""}`);
      setClusters(data.clusters || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load synonyms");
      setClusters([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPreview = useCallback(async (q) => {
    if (!q?.trim()) { setPreview(null); return; }
    try {
      const { data } = await api.get(`/synonyms/expand?q=${encodeURIComponent(q.trim())}`);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  }, []);

  const onSave = useCallback(async () => {
    if (!editing) return;
    const terms = editing.terms.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (!terms.length) { toast.error("At least one term is required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/synonyms", {
        cluster_id: editing.cluster_id,
        canonical: editing.canonical.trim(),
        terms,
      });
      toast.success(editing.cluster_id ? "Cluster updated" : "Cluster created");
      setEditing(null);
      await load(query);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }, [editing, load, query]);

  const onDelete = useCallback(async (cluster) => {
    if (!window.confirm(`Delete the ${cluster.canonical ? `“${cluster.canonical}”` : "selected"} cluster? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/synonyms/${cluster.cluster_id}`);
      toast.success("Cluster deleted");
      await load(query);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }, [load, query]);

  const onSeedAi = useCallback(async () => {
    const raw = window.prompt("How many clusters should the AI top-up add? (10–300)", "80");
    const count = Math.max(10, Math.min(parseInt(raw, 10) || 80, 300));
    setBusy(true);
    toast.info(`Asking the AI for ${count} clusters — this can take 30-60s…`);
    try {
      const { data } = await api.post("/admin/synonyms/seed-from-ai", { count });
      toast.success(`AI top-up: ${data.created} created · ${data.updated} merged · ${data.skipped} skipped`);
      await load(query);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI seed failed");
    } finally { setBusy(false); }
  }, [load, query]);

  const sources = useMemo(() => {
    const s = { ai: 0, admin: 0 };
    (clusters || []).forEach((c) => { if (c.source === "ai") s.ai += 1; else s.admin += 1; });
    return s;
  }, [clusters]);

  return (
    <div className="space-y-6" data-testid="admin-synonyms-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <h2 className="font-display font-black text-2xl text-rf-ink flex-1">Search synonyms</h2>
        <button
          type="button"
          onClick={() => setEditing({ canonical: "", terms: "" })}
          data-testid="synonyms-add"
          disabled={busy}
          className="btn-outline-ink !py-2 !px-3 text-sm"
        >
          <Plus size={16} /> Add cluster
        </button>
        <button
          type="button"
          onClick={onSeedAi}
          data-testid="synonyms-ai-seed"
          disabled={busy}
          className="btn-amber !py-2 !px-3 text-sm"
        >
          <Sparkle size={16} /> AI top-up
        </button>
      </div>

      <p className="text-sm text-rf-ink-soft">
        Teaches search that &ldquo;Drink&rdquo;, &ldquo;cocktails&rdquo;, &ldquo;Beverage&rdquo; or &ldquo;mocktail&rdquo; all mean the same thing — recipes, cookbooks and ingredients are expanded through these clusters at search time, together with plural / singular variants and fuzzy typo matching (Levenshtein ≤ 2).
      </p>

      <div className="rounded-2xl bg-white border border-rf-ink/10 p-5">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <label className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-rf-ink-soft" aria-hidden />
            <input
              type="text"
              placeholder="Filter clusters by any term…"
              data-testid="synonyms-filter"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onBlur={() => load(query)}
              onKeyDown={(e) => e.key === "Enter" && load(query)}
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-rf-ink/10 focus:border-rf-orange outline-none"
            />
          </label>
          <label className="relative flex-1">
            <Sparkle size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-rf-amber" aria-hidden />
            <input
              type="text"
              placeholder="Preview: what will search match for…?"
              data-testid="synonyms-preview-input"
              onBlur={(e) => onPreview(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onPreview(e.currentTarget.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-rf-ink/10 focus:border-rf-amber outline-none"
            />
          </label>
        </div>

        {preview && (
          <div className="rounded-xl bg-rf-cream p-4 mb-4 text-sm" data-testid="synonyms-preview">
            <p className="font-bold text-rf-ink mb-1">
              Search for <b>&ldquo;{preview.query}&rdquo;</b> matches {preview.count} term{preview.count === 1 ? "" : "s"}:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {preview.terms.map((t) => (
                <span key={t} className="text-xs font-bold px-2 py-0.5 rounded-full bg-white border border-rf-ink/10 text-rf-ink">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-rf-ink-soft mb-3">
          {clusters === null ? "Loading…" : `${clusters.length} cluster${clusters.length === 1 ? "" : "s"} · ${sources.ai} AI · ${sources.admin} admin`}
        </p>

        {clusters !== null && clusters.length === 0 && (
          <EmptyRow onAdd={() => setEditing({ canonical: "", terms: "" })} />
        )}

        {clusters !== null && clusters.length > 0 && (
          <ul className="divide-y divide-rf-ink/10" data-testid="synonyms-list">
            {clusters.map((c) => (
              <li
                key={c.cluster_id}
                data-testid={`synonym-row-${c.cluster_id}`}
                className="flex flex-col sm:flex-row sm:items-center gap-3 py-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-display font-black text-rf-ink">{c.canonical || "(no canonical)"}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(c.terms || []).map((t) => (
                      <span key={t} className={`text-xs font-bold px-2 py-0.5 rounded-full border ${t === c.canonical ? "bg-rf-amber/20 border-rf-amber text-rf-ink" : "bg-white border-rf-ink/10 text-rf-ink"}`}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${c.source === "ai" ? "bg-sky-50 text-sky-800 border border-sky-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`}>
                  {c.source || "admin"}
                </span>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    data-testid={`synonym-edit-${c.cluster_id}`}
                    onClick={() => setEditing({ cluster_id: c.cluster_id, canonical: c.canonical || "", terms: (c.terms || []).join(", ") })}
                    className="btn-outline-ink !py-1.5 !px-2.5 text-xs"
                  >
                    <PencilSimple size={14} /> Edit
                  </button>
                  <button
                    type="button"
                    data-testid={`synonym-delete-${c.cluster_id}`}
                    onClick={() => onDelete(c)}
                    className="btn-outline-ink !py-1.5 !px-2.5 text-xs !text-red-700 !border-red-300 hover:!bg-red-50"
                  >
                    <Trash size={14} /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-[60] bg-rf-ink/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setEditing(null)}
          data-testid="synonym-edit-modal"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <h3 className="font-display font-black text-2xl text-rf-ink flex-1">
                {editing.cluster_id ? "Edit cluster" : "New cluster"}
              </h3>
              <button type="button" onClick={() => !busy && setEditing(null)} className="w-9 h-9 rounded-full bg-rf-cream flex items-center justify-center" aria-label="Close">
                <X size={18} aria-hidden />
              </button>
            </div>

            <label className="block mb-3">
              <span className="text-xs font-bold uppercase tracking-wide text-rf-ink-soft">Canonical (most recognised form)</span>
              <input
                type="text"
                data-testid="synonym-edit-canonical"
                value={editing.canonical}
                onChange={(e) => setEditing((p) => ({ ...p, canonical: e.target.value }))}
                placeholder="drink"
                autoFocus
                disabled={busy}
                className="mt-1 w-full px-4 py-3 rounded-xl border-2 border-rf-ink/10 focus:border-rf-orange outline-none text-rf-ink"
              />
            </label>
            <label className="block mb-4">
              <span className="text-xs font-bold uppercase tracking-wide text-rf-ink-soft">Terms — one per line OR comma-separated</span>
              <textarea
                data-testid="synonym-edit-terms"
                value={editing.terms}
                onChange={(e) => setEditing((p) => ({ ...p, terms: e.target.value }))}
                placeholder={"drink\ncocktail\nbeverage\nmocktail"}
                rows={6}
                disabled={busy}
                className="mt-1 w-full px-4 py-3 rounded-xl border-2 border-rf-ink/10 focus:border-rf-orange outline-none text-rf-ink font-mono text-sm"
              />
              <span className="text-xs text-rf-ink-soft">
                Plural / singular forms are added automatically — no need to list both &ldquo;drink&rdquo; and &ldquo;drinks&rdquo;.
              </span>
            </label>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                data-testid="synonym-edit-cancel"
                onClick={() => setEditing(null)}
                disabled={busy}
                className="btn-outline-ink justify-center flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="synonym-edit-save"
                onClick={onSave}
                disabled={busy}
                className="justify-center flex-1 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-rf-orange hover:bg-rf-orange-dark text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? "Saving…" : (<><Check size={16} /> Save cluster</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
